import React, { useState } from 'react';
import {
  Box, Button, Card, CardActionArea, Chip, IconButton, ListItemText, Menu, MenuItem, Paper, Stack, Typography,
} from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import AddIcon from '@mui/icons-material/Add';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import { tripDetail, tripStatusColor, tripStatusText } from '../labels';
import { CardGrid, EmptyState, Loader, PageHeader, useLoad } from '../common';
import { isOpenTrip, tripTitle, useTripOptions, useTripPlanning } from './tripTools';

/** Shopping trips: open ones first, then the recent history (order as the server sends it). */
const TripsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: trips, error, loading, reload } = useLoad(() => api.getTrips());
  const planning = useTripPlanning(reload);
  const options = useTripOptions(() => reload());
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <PageHeader
        eyebrow="Compras"
        title="Idas"
        subtitle="Idas às compras, uma por loja"
        actions={<>
          <Button variant="contained" startIcon={<AddIcon />} onClick={planning.startNewTrip}>Nova ida</Button>
          <Button variant="outlined" startIcon={<CallSplitIcon />} onClick={planning.split}>Dividir por lojas</Button>
          <IconButton aria-label="Mais opções" onClick={e => setMoreAnchor(e.currentTarget)}><MoreVertIcon /></IconButton>
        </>}
      />
      <Menu anchorEl={moreAnchor} open={moreAnchor !== null} onClose={() => setMoreAnchor(null)}>
        <MenuItem onClick={() => { setMoreAnchor(null); navigate('/casa/compras/lojas'); }}>
          <ListItemText primary="Lojas" secondary="Onde compras e a ordem dos corredores" />
        </MenuItem>
      </Menu>

      <Loader loading={loading} error={error} onRetry={reload} hasData={trips !== undefined}>
        {trips && trips.length === 0 && (
          <Paper variant="outlined">
            <EmptyState
              icon={<LocalShippingOutlinedIcon />}
              title="Sem idas às compras"
              text="Uma ida leva o que está por comprar para uma loja. Começa uma nova, ou separa a lista por lojas."
              action={<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>«vou ao Continente amanhã»</Typography>}
            />
          </Paper>
        )}
        {trips && trips.length > 0 && (
          <CardGrid min={300}>
            {trips.map(trip => (
              <Card key={trip.publicId} sx={{ display: 'flex', alignItems: 'stretch', opacity: isOpenTrip(trip) ? 1 : 0.8 }}>
                <CardActionArea onClick={() => navigate(`/casa/idas/${trip.publicId}`)} sx={{ p: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 1.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2.5, flexShrink: 0, display: 'grid', placeItems: 'center', bgcolor: 'action.selected', color: 'primary.main' }}>
                    <StorefrontOutlinedIcon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Typography sx={{ fontWeight: 650 }}>{tripTitle(trip)}</Typography>
                      <Chip size="small" label={tripStatusText(trip)} color={tripStatusColor(trip)} variant="outlined" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{tripDetail(trip)}</Typography>
                  </Box>
                </CardActionArea>
                {isOpenTrip(trip) && (
                  <Box sx={{ p: 1 }}>
                    <IconButton aria-label="Opções da ida" onClick={() => options.open(trip, trip.cartTotal === 0)}><MoreVertIcon /></IconButton>
                  </Box>
                )}
              </Card>
            ))}
          </CardGrid>
        )}
      </Loader>
      {planning.element}
      {options.element}
    </>
  );
};

export default TripsPage;
